import type { Fields, ParcelProperties } from '~/models'

const PARCEL_DATA_KEY_LABELS: { [key: string]: string } = {
  parcelnumb: 'Parcel ID',
  address: 'Address',
  address2: 'Address 2',
  sunit: 'Unit',
  scity: 'City',
  county: 'County',
  state2: 'State Abbr',
  szip5: 'Zip Code',
  subdivision: 'Subdivision',
  lat: 'Latitude',
  lon: 'Longitude',
  // usecode: 'Land Use Code',
  zoning: 'Zoning',
  zoning_description: 'Zoning Desc',
  zoning_type: 'Zoning Type',
  zoning_subtype: 'Zoning Subtype',
  zoning_code_link: 'Zoning Code Link',
  qoz: 'Opportunity Zone',
  sqft: 'Square Feet',
  gisacre: 'Acres',
  ll_gisacre: 'Acres Calc',
  ll_bldg_footprint_sqft: 'Building Footprint Sqft',
  ll_bldg_count: '# of Buildings',
  yearbuilt: 'Year Built',
  numstories: 'Number of Stories',
  ll_address_count: '# of Addresses',
  numunits: '# of Units',
  taxyear: 'Tax Year',
  legaldesc: 'Legal Description',
  fema_flood_zone: 'FEMA Flood Zone',
  fema_flood_zone_subtype: 'FEMA Flood Zone Subtype',
  fema_flood_zone_data_date: 'FEMA Flood Zone Data Date',
  fema_nri_risk_rating: 'FEMA NRI Risk Rating',
  lowest_parcel_elevation: 'Lowest Elevation',
  highest_parcel_elevation: 'Highest Elevation',
  roughness_rating: 'Roughness Rating',
  transmission_line_distance: 'Transmission Line Distance',

  landval: 'Land Value',
  agval: 'Agricultural Value',
  improvval: 'Improvement Value',
  parval: 'Total Value',
  parvaltype: 'Parcel Value Type',
  saledate: 'Sale Date',
  saleprice: 'Sale Price',

  owner: 'Owner',
  mailadd: 'Owner Address',
  mail_city: 'Owner City',
  mail_state2: 'Owner State',
  mail_zip: 'Owner Zip Code',

  census_unified_school_district: 'Census Unified School District',
  rdi: 'Residential Delivery Indicator',
  usps_vacancy: 'USPS Vacancy',
  usps_vacancy_date: 'USPS Vacancy Date',
  housing_affordability_index: 'Housing Affordability Index',
  population_density: 'Population Density',
  population_growth_past_5_years: 'Population Growth (Past 5 Years)',
  population_growth_next_5_years: 'Population Growth (Next 5 Years)',
  housing_growth_past_5_years: 'Housing Growth (Past 5 Years)',
  housing_growth_next_5_years: 'Housing Growth (Next 5 Years)',
  household_income_growth_next_5_years:
    'Household Income Growth (Next 5 Years)',
  median_household_income: 'Median Household Income',

  sourceurl: 'Source URL',
  ll_updated_at: 'Updated Date',
}

const orderedKeys = Object.keys(PARCEL_DATA_KEY_LABELS)
const ignoredKeys = [
  'geoid',
  'fema_flood_zone_raw',
  'usecode',
  'parcelnumb_no_formatting',
  'll_gissqft',

  'original_address',
  'original_mailing_address',
  'saddno',
  'saddstr',
  'saddsttyp',
  'city',
  'szip',
  'szip5',
  'path',
  'zoning_id',
  'cass_errorno',
  'census_tract',
  'census_block',
  'census_blockgroup',
  'census_zcta',
  'unmodified_owner',
  'll_last_refresh',
  'll_stable_id',
  'll_uuid',
  'precisely_id',
  'placekey',
  'dpv_status',
  'dpv_codes',
  'dpv_notes',
  'dpv_type',
  'lbcs_activity',
  'lbcs_activity_desc',
  'lbcs_function',
  'lbcs_function_desc',
  'lbcs_structure',
  'lbcs_structure_desc',
  'lbcs_site',
  'lbcs_site_desc',
  'lbcs_ownership',
  'lbcs_ownership_desc',
  'address_source',
  'cass_errorno',
  'neighborhood_code',
  'ogc_fid',
  'plss_range',
  'plss_section',
  'plss_township',
  'saddstsuf',
  'cdl_date',
  'cdl_majority_category',
  'cdl_majority_percent',
  'cdl_raw',
  'qoz_tract',
  'book',
  'page',
  'lot',
  'saddpref',
  'll_stack_uuid',
]

type PropertyInformationFieldConfig = {
  key: string
  fallbackKeys?: string[]
  label?: string
}

const propertyInformationFieldConfigs: PropertyInformationFieldConfig[] = [
  { key: 'parcelnumb' },
  { key: 'zoning' },
  { key: 'zoning_description' },
  { key: 'zoning_type' },
  { key: 'zoning_subtype' },
  { key: 'qoz' },
  { key: 'gisacre', fallbackKeys: ['ll_gisacre'], label: 'Acres' },
  { key: 'sqft' },
  { key: 'll_bldg_footprint_sqft' },
  { key: 'll_bldg_count' },
  { key: 'yearbuilt' },
  { key: 'numstories' },
  { key: 'numunits' },
  { key: 'fema_flood_zone' },
  { key: 'fema_flood_zone_subtype' },
  { key: 'fema_nri_risk_rating' },
  { key: 'landval' },
  { key: 'improvval' },
  { key: 'agval' },
  { key: 'parval' },
  { key: 'taxyear' },
  { key: 'saleprice' },
  { key: 'saledate' },
  { key: 'owner' },
  { key: 'mailadd', label: 'Owner Address' },
  { key: 'mail_city', label: 'Owner City' },
  { key: 'mail_state2', label: 'Owner State' },
  { key: 'mail_zip', label: 'Owner Zip Code' },
  { key: 'legaldesc' },
]

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

const numberFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
})

const propertyInformationCurrencyKeys = new Set([
  'landval',
  'improvval',
  'agval',
  'parval',
  'saleprice',
])

const propertyInformationSquareFeetKeys = new Set([
  'sqft',
  'll_bldg_footprint_sqft',
])
const propertyInformationDateKeys = new Set(['saledate'])
const propertyInformationYearKeys = new Set(['yearbuilt', 'taxyear'])
const propertyInformationValueOverrides: {
  [key: string]: (value: unknown) => string | undefined
} = {
  saleprice: (value) => {
    const number = toFiniteNumber(value)
    if (number === 0) {
      return 'Undisclosed'
    }
    return undefined
  },
  parval: (value) => {
    const number = toFiniteNumber(value)
    if (number === 0) {
      return 'Undisclosed'
    }
    return undefined
  },
}

function hasValue(value: unknown) {
  if (value === null || value === undefined) {
    return false
  }
  if (typeof value === 'string') {
    return value.trim().length > 0
  }
  if (Array.isArray(value)) {
    return value.length > 0
  }
  if (typeof value === 'number') {
    return Number.isFinite(value)
  }
  return true
}

function toFiniteNumber(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  if (typeof value === 'string') {
    const normalized = value.replace(/,/g, '').trim()
    if (normalized.length === 0) {
      return undefined
    }
    const parsed = Number(normalized)
    if (Number.isFinite(parsed)) {
      return parsed
    }
  }
  return undefined
}

function normalizeOpportunityZone(value: unknown) {
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (['yes', 'y', 'true', '1'].includes(normalized)) {
      return 'Yes'
    }
    if (['no', 'n', 'false', '0'].includes(normalized)) {
      return 'No'
    }
    return value
  }
  return value
}

function formatDate(value: unknown) {
  if (typeof value !== 'string') {
    return undefined
  }
  const parsed = new Date(value)
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleDateString('en-US')
  }
  return value
}

function formatPropertyInformationValue(key: string, value: unknown) {
  const override = propertyInformationValueOverrides[key]?.(value)
  if (override !== undefined) {
    return override
  }
  if (key === 'qoz') {
    return normalizeOpportunityZone(value)
  }
  if (propertyInformationCurrencyKeys.has(key)) {
    const number = toFiniteNumber(value)
    if (number !== undefined) {
      return currencyFormatter.format(number)
    }
  }
  if (propertyInformationSquareFeetKeys.has(key)) {
    const number = toFiniteNumber(value)
    if (number !== undefined) {
      return `${numberFormatter.format(number)} SF`
    }
  }
  if (propertyInformationDateKeys.has(key)) {
    const date = formatDate(value)
    if (date) {
      return date
    }
  }
  if (propertyInformationYearKeys.has(key)) {
    const year = toFiniteNumber(value)
    if (year !== undefined) {
      return Math.trunc(year).toString()
    }
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }
  if (typeof value === 'number') {
    return numberFormatter.format(value)
  }
  if (typeof value === 'string') {
    return value
  }
  return String(value)
}

export function getParcelLabelValues(parcelData: ParcelProperties) {
  const matchingKeys: string[] = []
  const nonMatchingKeys: string[] = []

  Object.keys(parcelData.fields).forEach((key) => {
    if (key in PARCEL_DATA_KEY_LABELS) {
      matchingKeys.push(key)
    } else if (!ignoredKeys.includes(key)) {
      console.info('Key missing label', key)
      nonMatchingKeys.push(key)
    }
  })

  matchingKeys.sort((a, b) => orderedKeys.indexOf(a) - orderedKeys.indexOf(b))
  nonMatchingKeys.sort()

  return matchingKeys.concat(nonMatchingKeys).map((key) => ({
    key,
    label: PARCEL_DATA_KEY_LABELS[key] || key, //.replace(/_/g, ' '), //camelCaseToWords(key.replace(/_/g, ' ')),
    value: parcelData.fields[key as keyof Fields],
  }))
}

function getPropertyFieldValue(
  fields: Record<string, unknown>,
  config: PropertyInformationFieldConfig
) {
  const keys = [config.key, ...(config.fallbackKeys || [])]
  for (const key of keys) {
    const value = fields[key]
    if (hasValue(value)) {
      return value
    }
  }
  return undefined
}

export function getPropertyInformationLabelValues(
  parcelData?: ParcelProperties | null
) {
  if (!parcelData) {
    return []
  }

  const fields = parcelData.fields as Record<string, unknown>

  return propertyInformationFieldConfigs
    .map((config) => {
      const value = getPropertyFieldValue(fields, config)
      if (!hasValue(value)) {
        return null
      }
      const key = config.key
      return {
        key,
        label: config.label || PARCEL_DATA_KEY_LABELS[key] || key,
        value: formatPropertyInformationValue(key, value),
      }
    })
    .filter(
      (
        value
      ): value is {
        key: string
        label: string
        value: string
      } => value !== null
    )
}
